import React, { useState, useEffect } from 'react'
import Modal from "@mui/material/Modal";
import closeIcon from "../../../../../Assets/close-icon.svg"
import { useDispatch, useSelector } from 'react-redux';
import { SET_MODAL } from '../../../../../redux/actionTypes/modalActionType';
import Button from '../../../../Button/Button';
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { serviceConsumer } from '../../../../../network/ServiceConsumer';
import Loader from "../../../../Loader/Loader";
import { groupAndSortComp } from '../../../../../helper/SortSyllabus';

// === OLD MERGE FLOW (commented out — DO NOT DELETE) ===
// TO REVERT: Change the component signature back to ({ modalState, tableArr, competitionId })
//            and uncomment the old selectCompetitorHandler below.
//            Then comment out the NEW MERGE FLOW sections.
//
// Old component signature:
// const MergeModal = ({ modalState, tableArr, competitionId }) => {
//
// Old submit handler (uses pre-checked tableArr from MergeSplitFn.jsx):
// const selectCompetitorHandler = async (values) => {
//     const checkedArray = tableArr.filter((item) => item.checked)
//     const registrationIds = checkedArray.map((item => item._id))
//     const payload = {
//         origin_competition: competitionId,
//         destination_competition: values?.competition,
//         registrations: registrationIds,
//     }
//     try {
//         const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/merge`
//         setLoading(true)
//         const response = await serviceConsumer("POST", url, payload)
//         setSuccess("Competitors merged successfully")
//         setTimeout(() => window.location.reload(), 1000)
//         setLoading(false)
//     } catch (error) {
//         setLoading(false)
//         setError(error.messages)
//     }
// }
//
// Old modal close handler (uses Redux dispatch):
// const modalCloseHandler = () => {
//     dispatch({
//         type: SET_MODAL,
//         payload: { modal: "", eventProp: "" }
//     })
// }
// === END OLD MERGE FLOW ===

// === NEW MERGE FLOW (added March 2026) ===
// This version auto-fetches all dancers and selects them all automatically.
// Props: modalState, competition (full object), eventId, onClose, onSuccess
// TO REVERT TO OLD: Comment out this component and uncomment the OLD MERGE FLOW above.
//                   Update MergeSplit.jsx to use the old edit-icon flow (see MergeSplit.jsx OLD FLOW comments).
//                   The old MergeModal expects (modalState, tableArr, competitionId) props from MergeSplitFn.jsx.

const MergeModal = ({ modalState, competition, eventId, onClose, onSuccess, tableArr, competitionId }) => {

    const dispatch = useDispatch();
    const [competitions, setCompetitions] = useState([])
    const [allDancers, setAllDancers] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const modal = useSelector((state) => state.modalReducer);

    // Determine which mode we're in: new (competition prop) or old (tableArr prop)
    const isNewFlow = !!competition
    const resolvedEventId = isNewFlow ? eventId : modal.eventId
    const resolvedCompetitionId = isNewFlow ? competition._id : competitionId

    // get competition details for select dropdown values
    const getCompetitions = async () => {
        try {
            const url = `${process.env.REACT_APP_BASE_URL}/feis/${resolvedEventId}/competition`
            setLoading(true)
            const response = await serviceConsumer("GET", url)
            const sortedCompetitions = groupAndSortComp(response.competitions)
            // Filter out the source competition from the dropdown
            const filtered = sortedCompetitions.filter(c => c._id !== resolvedCompetitionId)
            setCompetitions(filtered)
            setLoading(false)
        } catch (error) {
            setError(error.messages)
            setLoading(false)
        }
    }

    // === NEW MERGE FLOW: Auto-fetch all dancers ===
    const fetchAllDancers = async () => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/dancers/${resolvedCompetitionId}`
            const response = await serviceConsumer("GET", url)
            setAllDancers(response.result || [])
            setLoading(false)
        } catch (err) {
            setError(err.messages || "Failed to fetch dancers")
            setLoading(false)
        }
    }

    useEffect(() => {
        if (modalState) {
            getCompetitions()
            if (isNewFlow) {
                fetchAllDancers()
            }
        }
    }, [modalState])

    //formik initial values
    const selectCompetitorInitialValues = {
        competition: ""
    }

    //formik validations
    const selectCompetitorValidationSchema = Yup.object().shape({
        competition: Yup.string()
            .trim()
            .label("Gender:")
            .required("This field is required.")
            .nullable(),
    })

    // === NEW MERGE FLOW: Submit handler (auto-selects all dancers) ===
    const selectCompetitorHandler = async (values) => {
        let registrationIds;
        if (isNewFlow) {
            // New flow: use all fetched dancers (auto-selected)
            registrationIds = allDancers.map(dancer => dancer._id)
        } else {
            // Old flow fallback: use pre-checked tableArr
            const checkedArray = tableArr.filter((item) => item.checked)
            registrationIds = checkedArray.map((item => item._id))
        }

        const payload = {
            origin_competition: resolvedCompetitionId,
            destination_competition: values?.competition,
            registrations: registrationIds,
        }
        try {
            const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/merge`
            setLoading(true)
            const response = await serviceConsumer("POST", url, payload)
            setSuccess("Competitors merged successfully")
            setTimeout(() => {
                if (isNewFlow) {
                    onSuccess && onSuccess()
                    onClose && onClose()
                } else {
                    window.location.reload()
                }
            }, 1000)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            setError(error.messages)
        }
    }

    //modal close handler - supports both old (Redux) and new (callback) flows
    const modalCloseHandler = () => {
        if (isNewFlow) {
            onClose && onClose()
        } else {
            dispatch({
                type: SET_MODAL,
                payload: {
                    modal: "",
                    eventProp: "",
                }
            })
        }
    }

    const changeHandler = (e, name, setFieldValue) => {
        setFieldValue(name, e.target.value);
        setError("");
        setSuccess("")
    };

    return (
        <>
            {loading && <Loader />}
            <Modal sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "10px" }} open={modalState}>
                <div className="org-modal-div merge-modal-div" >
                    <div className="close-div mb-1" style={{ padding: "0px 10px 0px 0px" }}>
                        <img src={closeIcon} alt="close-icon" className="close-icon" onClick={modalCloseHandler} />
                    </div>
                    <div style={{ width: "250px" }}>
                        <p className="modal-org-title text-center my-2">Merge into</p>
                        <p className="modal-org-subtitle text-center">
                            {/* === NEW MERGE FLOW: Show source competition info === */}
                            {isNewFlow
                                ? `Merge all ${allDancers.length} dancers from ${competition?.code}-${competition?.description} into another competition.`
                                : "Merge dancers with other competition."
                            }
                        </p>
                        <div className="d-flex flex-column align-items-center ">
                            <Formik
                                initialValues={selectCompetitorInitialValues}
                                validationSchema={selectCompetitorValidationSchema}
                                onSubmit={selectCompetitorHandler}
                            >
                                {({ setFieldValue, dirty, isValid }) => {
                                    return (
                                        <Form autoComplete="off">
                                            <div>
                                                <div className="d-flex flex-column">
                                                    <label className="label-head">Choose competition:</label>
                                                    <Field name="competition" as="select" className="select-box" onChange={(e) => {
                                                        changeHandler(e, "competition", setFieldValue);
                                                    }} >
                                                        <option defaultValue hidden >Select</option>
                                                        {competitions.map(competition => <option key={competition?._id} value={competition?._id}>{competition?.code}-{competition?.description}</option>)}
                                                    </Field>
                                                    <ErrorMessage className="auth-error" name="competition" component="div" />
                                                </div>
                                                {success && <div style={{ color: "green", fontSize: "12px" }}>{success}</div>}
                                                {error && <div style={{ color: "red", fontSize: "12px" }}>{error}</div>}
                                                <div className="d-flex flex-grow-1 justify-content-center mt-3">
                                                    <Button type="submit" className={isValid && dirty ? "filled" : "disabled"} > Merge</Button>
                                                </div>
                                            </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal >
        </>
    )
}
// === END NEW MERGE FLOW ===

export default MergeModal
